import {
    ApplicationConfig,
    enableProdMode,
    inject,
    provideAppInitializer,
    provideZonelessChangeDetection
} from '@angular/core';
import {provideRouter, RouteReuseStrategy, withComponentInputBinding, withRouterConfig} from '@angular/router';

import {routes} from './app.routes';
import {ApiConfiguration} from './restclient/api.config';
import {AppRouteReuseStrategy} from './app-routing-reuse-strategy';
import {AppConstants, AppEnvironment} from './app.constants';
import * as wjCore from '@mescius/wijmo';
import {changeCulture, culture} from '@mescius/wijmo';
import {initWijmoElements} from './data/data-maps';
import {AuthService} from './security/auth.service';
import {AppDevToolsService} from './service/app-devtools.service';
import {AccessControlService} from './security/access-control.service';
import {Alerts} from './lib/alerts';
import {MyProfileApi} from '../api-client-kt/src';

async function appInitializer(): Promise<void> {
    // inject services BEFORE 'await'ing any async operations
    // otherwise we'll see "function must be called from an injection context" errors
    // because the injection context is lost after the first 'await'
    const devToolsService = inject(AppDevToolsService);
    const authService = inject(AuthService);
    const accessControlService = inject(AccessControlService);
    const myProfileApi = inject(MyProfileApi)

    // The initialization must start with fetching the environment configuration data from the server
    const envRequest = await fetch('/env.json');
    if (envRequest.ok) {
        const envData = await envRequest.json();
        Object.assign(AppEnvironment, envData);
    } else {
        console.warn('Could not load environment configuration from /env.json');
    }

    // is the app in maintenance mode? if so, redirect to the specified URL (could be a maintenance page or the company website)
    if (AppEnvironment.outOfServiceRedirectUrl != null) {
        document.location.href = AppEnvironment.outOfServiceRedirectUrl;
        throw new Error('App is out of service');
    }

    // initialize Wijmo library with license key
    wjCore.setLicenseKey(AppEnvironment.wijmoKey);
    initWijmoElements();
    await changeCulture(AppConstants.getUserLocale());
    console.log('Wijmo culture set to ' + AppConstants.getUserLocale());
    console.log(culture.Globalize.numberFormat['.']);

    // initialize authentication service ... that will make sure
    // we have the auth tokens ready before the app starts
    await authService.initialize();

    // load the user profile data and setup the access-control service
    try {
        const myProfile = await myProfileApi.getMyProfileRaw();
        accessControlService.initFromProfile(await myProfile.value());
    } catch (err) {
        await Alerts.alertPopup({
            body: `ERROR: Could not load user profile. Maybe your account is not enabled yet?
            Please contact the system administrator.`
        });
    }

    // enableProdMode();
}

export function appConfig(): ApplicationConfig {
    return {
        providers: [
            provideAppInitializer(appInitializer),
            provideZonelessChangeDetection(),
            provideRouter(routes, withComponentInputBinding(), withRouterConfig({paramsInheritanceStrategy: 'always'})),
            { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },

            ...ApiConfiguration.initApiProviders()
        ]
    }
}
